import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { readonly, useRoute, useRuntimeConfig, useState } from '#imports';
import { getMidByPriority, validateMid } from '../utils/mid';

export default function useMid(route?: RouteLocationNormalizedGeneric) {
  const activeMid = useState('activeMid', () => '');
  const midPinned = useState('midPinned', () => false);
  const config = useRuntimeConfig();

  try {
    const routeToUse = route || useRoute();
    const queryMid = typeof routeToUse.query.mid === 'string' ? routeToUse.query.mid : '';
    const midFromUrl = !midPinned.value && validateMid(queryMid) ? queryMid : null;
    const defaultMid = typeof config.public.DEFAULT_MID === 'string' ? config.public.DEFAULT_MID : '';

    const midByPriority = getMidByPriority({
      activeMid: activeMid.value,
      midFromUrl,
      defaultMid,
    });

    if (midByPriority !== activeMid.value) {
      activeMid.value = midByPriority;
    }
  } catch {
    // Do nothing, only set the MID if it is valid and make sure the application does not crash
  }

  /**
   * Sets the active MID. Validates the MID before setting, silently ignores invalid values.
   */
  function setMid(newMid: string): void {
    if (validateMid(newMid)) {
      activeMid.value = newMid;
    }
  }

  /**
   * Resets the active MID to the provided default or falls back to config.public.DEFAULT_MID.
   */
  function resetMid(defaultMid?: string): void {
    const fallback = typeof config.public.DEFAULT_MID === 'string' ? config.public.DEFAULT_MID : '';
    activeMid.value = defaultMid ?? fallback;
  }

  /**
   * The MID (Marketing Identifier) value.
   * It is either extracted from the URL or falls back to the default MID from the configuration.
   * The default MID can be different for Belgium versions of a site.
   */
  return { mid: readonly(activeMid), setMid, resetMid };
}
